import axios, {AxiosError} from 'axios'
import {FieldArray, FormikProvider, useFormik, yupToFormErrors} from 'formik'
import React, {useEffect, useState} from 'react'
import {Card} from 'react-bootstrap'
import * as Yup from 'yup'
import * as a from '../../../../../_ancientLibrary/components/'
import {MODAL_TYPES, useMessageModalContext} from '../../../../../_ancientLibrary/utilities'
import {isValidDateFormat} from '../../../../../_ancientLibrary/utilities/validators'

import {CollectionHeader} from '../../../../_models/collectionModels'
import {GARBAGE_ICON} from './SpreadsheetManagerModal'

export const SAVE_TYPE = {
  EMPTY: '',
  DRAFT: 'draft',
  PUBLISH: 'publish',
  PUBLISH_ADMIN: 'publish_admin',
} as const

export type SaveType = typeof SAVE_TYPE[keyof typeof SAVE_TYPE]

interface Props {
  checkErrors?: any
  collection: CollectionHeader
  collectionYear?: Date
  data: any | null
  isDeleteButtonDisabled?: boolean
  isInputsDisabled?: boolean
  onIsSubmitting?: any
  onRowRemove?: (index: number) => void
  onSpreadsheetChange?: any
  onSubmitted: (saveType: SaveType) => void
  saveType?: SaveType
  submitRef: any
}

interface Schema {
  itemID: number
  name: string
  calculation: string
  sort: number
  dataType: number
  settingsTable: string
  helpText: string
}
const API_URL = process.env.REACT_APP_API_URL
type FormikErrorsData = {[index: string]: {[calculation: string]: string}}
type FormikTouchedData = {[index: string]: {[calculation: string]: boolean}}

const validatePercentageSum = function (_: number | null | undefined, context: Yup.TestContext) {
  const values = context.parent
  const jobFields = ['job_a', 'job_b', 'job_c']
  const jobPercentages: number[] = []

  jobFields.forEach((job) => {
    const codeField = `${job}_code`
    const percentField = `${job}_percent`

    if (values[codeField] && values[percentField] != null && !Number.isNaN(values[percentField])) {
      jobPercentages.push(Number(values[percentField]))
    }
  })

  if (jobPercentages.length > 1) {
    const sum = jobPercentages.reduce((acc, curr) => acc + curr, 0)
    if (sum !== 100) {
      return context.createError({
        message: `Job percentages must add up to 100% (currently ${sum}%)`,
      })
    }
  }

  if (jobPercentages.length === 1) {
    if (jobPercentages[0] !== 100) {
      return context.createError({
        message: `Job percentage must be 100%`,
      })
    }
  }

  return true
}

const transformValueToNullIfEmpty = (value: any, originalValue: any) => {
  // If the original value is null/undefined/empty string, keep it as null
  if (originalValue == null || originalValue === '' || Number.isNaN(originalValue)) {
    return null
  }
  return value
}
const editCollectionSchema = Yup.array().of(
  Yup.object().shape({
    employee_reference: Yup.string()
      .min(1, 'Minimum 3 symbols')
      .max(100, 'Maximum 100 symbols')
      .required('Employee reference is required')
      .test('employee_reference', 'Employee code must be unique', function (value, context) {
        const firstIndex = context.options.context?.values.findIndex(
          (x: any) => x.employee_reference === value
        )
        const lastIndex = context.options.context?.values.findLastIndex(
          (x: any) => x.employee_reference === value
        )
        return firstIndex === lastIndex
      }),
    salaried: Yup.string().test(
      'isYes',
      'Must be "Yes" or "No"',
      (value) => value?.toLowerCase() === 'yes' || value?.toLowerCase() === 'no'
    ),
    hire_date: Yup.date().typeError('Invalid Date'),
    termination_date: Yup.date()
      .typeError('Invalid Date')
      .test(
        'valid-termination-date',
        'Termination date must be within the labor year',
        function (value) {
          // Check if termination_date is null or undefined
          if (!value) {
            return true
          }

          // Extract the year from the termination_date
          const terminationYear = value.getFullYear()

          const spreadSheetYear = new Date(this.options.context?.collectionYear).getFullYear()

          // Check if termination_year is greater than or equal to labor_year
          // return terminationYear <= labor_year
          return terminationYear === spreadSheetYear
        }
      )
      .test('valid-termination-date-format', 'Invalid date format (MM/DD/YYYY)', function (value) {
        if (!value) {
          return true // Allow empty value if it's not required
        }

        // Use the isValidDateFormat function to check the format
        return isValidDateFormat(new Date(value).toLocaleDateString())
      }),
    base_wages: Yup.number().nullable().typeError('Not a number'),
    bonus_incentives: Yup.number()
      .nullable()
      .typeError('Not a number')
      .min(0, 'Must be greater than 0'),
    total_hours: Yup.number()
      .nullable()
      .typeError('Not a number')
      .min(0, 'Minimum 3 symbols')
      .max(8760, 'Maximum 8760'),
    overtime_hours: Yup.number()
      .nullable()
      .typeError('Not a number')
      .min(0, 'Must be greater than 0')
      .max(8760, 'Maximum 8760'),
    overtime_wages: Yup.number()
      .nullable()
      .typeError('Not a number')
      .min(0, 'Must be zero or more'),

    job_a_code: Yup.number()
      .nullable()
      .transform(transformValueToNullIfEmpty)
      .typeError('Not a number')
      .min(0, 'Must be zero or more')
      .max(999, 'Must be less than 1000'),
    job_a_percent: Yup.number()
      .nullable()
      .transform(transformValueToNullIfEmpty)
      .when('job_a_code', {
        is: (val: number) => val !== null,
        then: (schema) =>
          schema
            .required('Job A code must have a percent')
            .typeError('Not a number')
            .min(0, 'Must be 0% or more')
            .max(100, 'Must be 100% or less'),
        otherwise: (schema) => schema.nullable(),
      })
      .test('has-code', 'Job A code is required when percent is provided', function (value) {
        if (value != null) {
          return this.parent.job_a_code != null
        }
        return true
      })
      .test('percentage-sum', validatePercentageSum),

    job_b_code: Yup.number()
      .nullable()
      .typeError('Not a number')
      .min(0, 'Must be zero or more')
      .max(999, 'Must be less than 1000')
      .transform(transformValueToNullIfEmpty),
    job_b_percent: Yup.number()
      .nullable()
      .transform(transformValueToNullIfEmpty)
      .when('job_b_code', {
        is: (val: number) => val !== null,
        then: (schema) =>
          schema
            .required('Job B code must have a percent')
            .typeError('Not a number')
            .min(0, 'Must be 0% or more')
            .max(100, 'Must be 100% or less'),
        otherwise: (schema) => schema.nullable(),
      })
      .test('has-code', 'Job B code is required when percent is provided', function (value) {
        if (value != null) {
          return this.parent.job_b_code != null
        }
        return true
      })
      .test('percentage-sum', validatePercentageSum),

    job_c_code: Yup.number()
      .nullable()
      .typeError('Not a number')
      .min(0, 'Must be zero or more')
      .max(999, 'Must be less than 1000')
      .transform(transformValueToNullIfEmpty),
    job_c_percent: Yup.number()
      .nullable()
      .transform(transformValueToNullIfEmpty)
      .when('job_c_code', {
        is: (val: number) => val !== null,
        then: (schema) =>
          schema
            .required('Job C code must have a percent')
            .typeError('Not a number')
            .min(0, 'Must be 0% or more')
            .max(100, 'Must be 100% or less'),
        otherwise: (schema) => schema.nullable(),
      })
      .test('has-code', 'Job C code is required when percent is provided', function (value) {
        if (value != null) {
          return this.parent.job_c_code != null
        }
        return true
      })
      .test('percentage-sum', validatePercentageSum),

    nc_comp_1: Yup.string().nullable(),
    nc_comp_1_value: Yup.number()
      .nullable()
      .typeError('Not a number')
      .min(0, 'Must be zero or more'),

    nc_comp_2: Yup.string().nullable(),
    nc_comp_2_value: Yup.number()
      .nullable()
      .typeError('Not a number')
      .min(0, 'Must be zero or more'),

    nc_comp_3: Yup.string().nullable(),
    nc_comp_3_value: Yup.number()
      .nullable()
      .typeError('Not a number')
      .min(0, 'Must be zero or more'),
  })
)

export const SpreadsheetDataTable: React.FC<Props> = ({
  checkErrors,
  collection,
  collectionYear,
  data,
  isInputsDisabled,
  isDeleteButtonDisabled,
  onIsSubmitting,
  onRowRemove,
  onSubmitted,
  saveType,
  submitRef,
}) => {
  const {showModal} = useMessageModalContext()
  const [submittingRowIndex, setSubmittingRowIndex] = useState<number | null>(null)

  const formik = useFormik({
    initialValues: data.data,
    enableReinitialize: true,
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    validate: async (values) => {
      try {
        await editCollectionSchema.validate(values, {
          abortEarly: false,
          context: {collectionYear, values},
        })
        checkErrors(true)
        return {}
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = yupToFormErrors(err)
          checkErrors(false)
          return errors
        }
        console.error(err)
        return {}
      }
    },
    onSubmit: async (values, {setSubmitting}) => {
      setSubmitting(true)
      await saveSpreadsheet(values)
      setSubmitting(false)
    },
  })

  useEffect(() => {
    if (formik) {
      formik.validateForm()
    }
  }, [])

  const handleSpreadsheetDataChange = async (e: any) => {
    e.preventDefault()
    const errors = await formik.validateForm()
    const hasValidationErrors = Object.keys(errors).length > 0

    if (!hasValidationErrors) {
      await formik.submitForm()
    } else {
      if (saveType === SAVE_TYPE.PUBLISH || saveType === SAVE_TYPE.PUBLISH_ADMIN) {
        showModal(MODAL_TYPES.ERROR_MODAL, {
          userMessage: `Errors are present. Cannot publish the spreadsheet.`,
        })
      } else {
        await saveSpreadsheet(formik.values)
      }
    }
  }

  const handleRowRemove = async (indexColumn: number) => {
    setSubmittingRowIndex(indexColumn)
    onRowRemove?.(indexColumn)
    const newValues = formik.values.filter((_: any, i: number) => i !== indexColumn)
    await saveSpreadsheet(newValues)
    setSubmittingRowIndex(null)
  }

  const saveSpreadsheet = async (spreadsheet: any): Promise<any | undefined> => {
    const submittedSaveType = saveType || SAVE_TYPE.DRAFT
    let didSucceed = false

    try {
      onIsSubmitting(true)
      console.log(formik.errors)
      let errorCount = Object.keys(formik.errors).length
      ;(await axios.put(`${API_URL}/bm/CollectionsDetail/saveCollection`, {
        ProcessID: collection.processID,
        data: spreadsheet,
        saveType: submittedSaveType,
        errorCount: errorCount,
      })) as any
      didSucceed = true
      showModal(MODAL_TYPES.SUCCESS_TOAST, {message: `The spreadsheet has been saved.`})
      onSubmitted(submittedSaveType)
      //Todo: Callback
    } catch (exception) {
      const err = exception as AxiosError
      let error = {
        title: 'Error',
        userMessage: 'An error occurred, please contact Benchmarking Administration.',
        errorCleanMessage: err.message,
        stackTrace: '',
      }
      if (err?.response?.data != undefined) error = err.response.data
      showModal(MODAL_TYPES.ERROR_MODAL, error)
    } finally {
      onIsSubmitting(false)
      if (!didSucceed) {
        onSubmitted(SAVE_TYPE.EMPTY)
      }
    }
  }

  const formatValue = (
    value: string,
    dataType: number,
    settingsTable: string,
    calculation: string,
    index: number
  ) => {
    let isDataGood = true
    let dangerColor = 'bg-warning'
    let color = ''
    let textAlign = ''
    let formattedValue = ''
    try {
      switch (dataType) {
        case 1:
          formattedValue = value
          break
        case 2:
          formattedValue = parseFloat(value).toFixed(2)
          isDataGood = formattedValue !== 'NaN'
          textAlign = 'text-center'
          break
        case 3:
          formattedValue = parseFloat(value).toFixed(2)
          isDataGood = formattedValue !== 'NaN'
          textAlign = 'text-center'
          break
        case 11:
          formattedValue = parseFloat(value).toFixed(5)
          isDataGood = formattedValue !== 'NaN'
          textAlign = 'text-center'
          break
        case 4:
          formattedValue = new Date(value).toLocaleDateString()
          isDataGood = formattedValue !== 'Invalid Date'
          textAlign = 'text-center'
          break
        case 5:
          let valueLower = value.toLowerCase()
          formattedValue = value === 'Yes' ? 'Yes' : 'No'
          isDataGood =
            valueLower === 'yes' || valueLower === '1' || valueLower === 'no' || valueLower === '0'
          textAlign = 'text-center'
          break
        default:
          formattedValue = value
          break
      }

      formattedValue = isDataGood ? formattedValue : value
    } catch (error) {
      formattedValue = value
      color = dangerColor
    }

    if ((formik.errors as FormikErrorsData)[index]?.[calculation]) color = dangerColor

    switch (dataType) {
      case 8:
        //SettingsDropDown
        return (
          <a.TD key={`${index}.${calculation}`} className={`${textAlign}`} backgroundColor={color}>
            <a.AncientDropdownInstanceSetting
              dropdownData={settingsTable}
              disabled={isInputsDisabled}
              errors={(formik.errors as FormikErrorsData)[index]?.[calculation]}
              fieldProps={formik.getFieldProps(`${index}.${calculation}`)}
              touched={(formik.touched as FormikTouchedData)[index]?.[calculation]}
              valueDescription={'settingDescription'}
              valueType={'settingName'}
              verticalLabel={true}
            />
          </a.TD>
        )
      default:
        return (
          <a.TD key={`${index}.${calculation}`} className={`${textAlign}`} backgroundColor={color}>
            <a.AncientInputText
              fieldProps={formik.getFieldProps(`${index}.${calculation}`)}
              touched={(formik.touched as FormikTouchedData)[index]?.[calculation]}
              errors={(formik.errors as FormikErrorsData)[index]?.[calculation]}
              errorPlaceholder={true}
              disabled={isInputsDisabled}
            />
          </a.TD>
        )
    }
  }

  return (
    <>
      {formik.values && formik.values.length > 0 ? (
        <form
          id='spreadsheet'
          className='form overflow-scroll'
          ref={submitRef}
          onSubmit={(event) => {
            handleSpreadsheetDataChange(event)
          }}
          noValidate
        >
          <FormikProvider value={formik}>
            <FieldArray
              name='inputRuleList'
              render={() => (
                <>
                  <a.AncientTable id='collectionSpreadsheet' className=''>
                    <a.THead>
                      <a.TR contentType='head'>
                        <a.TH style={{width: '100px'}}></a.TH>
                        {data.schema.map((schema: Schema) => (
                          <a.TH
                            helpText={schema.helpText}
                            key={schema.calculation}
                            style={{minWidth: '150px'}}
                          >
                            {schema.calculation}
                          </a.TH>
                        ))}
                      </a.TR>
                    </a.THead>
                    <a.TBody>
                      {formik.values.map((item: any, indexColumn: number) => {
                        return (
                          <a.TR key={indexColumn}>
                            <a.TD style={{width: '100px'}}>
                              <a.AncientButton
                                icon={GARBAGE_ICON}
                                onClick={() => handleRowRemove(indexColumn)}
                                className='btn-danger btn-sm'
                                confirmActionMessage='Are you sure you want to delete this row?'
                                cssStyle={{width: '56px'}}
                                submitting={submittingRowIndex === indexColumn}
                                disabled={isDeleteButtonDisabled}
                              />
                            </a.TD>
                            {data.schema.map((schema: Schema) => {
                              return formatValue(
                                item[schema.calculation as string] as string,
                                schema.dataType,
                                schema.settingsTable,
                                schema.calculation,
                                indexColumn
                              )
                            })}
                          </a.TR>
                        )
                      })}
                    </a.TBody>
                  </a.AncientTable>
                </>
              )}
            />
          </FormikProvider>
        </form>
      ) : (
        <>
          <div className='row pb-12'>
            <div className='col-lg-12 flex-center'>
              <Card bg={'secondary'}>
                <Card.Body>
                  <Card.Text>
                    <p>
                      This spreadsheet is currently empty. To get started, you must import a
                      populated spreadsheet.
                    </p>
                    <ol>
                      <li>
                        Click the <strong>"Download Template"</strong> button to obtain an Excel
                        spreadsheet version of the wage survey.
                      </li>
                      <li>Open the spreadsheet and enter the necessary employee wage details.</li>
                      <li>Save the updated spreadsheet on your device.</li>
                      <li>
                        Click <strong>"Choose File"</strong>, then select the spreadsheet you just
                        updated.
                      </li>
                      <li>Your data will be uploaded and displayed on this page.</li>
                    </ol>
                    <p>
                      Once uploaded, you can review the information for errors before saving or
                      publishing.
                    </p>
                  </Card.Text>
                </Card.Body>
              </Card>
            </div>
          </div>
        </>
      )}
    </>
  )
}
